/**
 * The `a` renderer for agent markdown: an entity path becomes an `EntityRef`
 * citation; anything else is an ordinary link that opens in a new tab.
 *
 * `CHAT_COMPONENTS` is module-level so its identity is stable: `MessageResponse`
 * is memoised on its children, and Streamdown re-parses when `components`
 * changes.
 */
import type { AnchorHTMLAttributes, FC } from "react";

import { parseEntityHref } from "./entityRefs";
import { EntityRef } from "./EntityRef";

/** The href Streamdown gives a link still being streamed. */
const INCOMPLETE_HREF = "streamdown:incomplete-link";

/**
 * A link the model wrapped in inline code (`` `[Letter](/cases/…)` ``), which
 * renders as raw markdown, unwrapped; other code is left alone.
 */
export const unwrapCodeLinks = (text: string): string =>
  text.replace(/`+\s*(\[[^\]\n]+\]\([^)\s]+(?:\s+"[^"\n]*")?\))\s*`+/g, "$1");

type Props = AnchorHTMLAttributes<HTMLAnchorElement> & {
  /** The hast node Streamdown passes along; not rendered. */
  node?: unknown;
};

export const ChatLink: FC<Props> = ({
  href,
  title,
  children,
  node: _node,
  ...rest
}) => {
  if (!href || href === INCOMPLETE_HREF) {
    // Still arriving (or nowhere to go): the label, not a dead link.
    return <span className="text-fg-2">{children}</span>;
  }
  const target = parseEntityHref(href);
  if (target) {
    return (
      <EntityRef target={target} title={title}>
        {children}
      </EntityRef>
    );
  }
  return (
    <a
      href={href}
      title={title}
      target="_blank"
      rel="noreferrer noopener"
      className="text-primary underline underline-offset-2"
      {...rest}
    >
      {children}
    </a>
  );
};

/** Pass to `MessageResponse` / Streamdown as `components`. */
export const CHAT_COMPONENTS = { a: ChatLink } as const;
